<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Geographic Exposure
                            </h2>
                        </div>
                    </div>

                    <!-- Denied state -->
                    <zen-denied-state
                        v-if="!canView"
                        title="Geographic exposure restricted"
                        message="Geographic exposure needs a higher plan — no sales call, just upgrade in Billing."
                    />

                    <template v-if="canView">
                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="geo-exposure-filter-from"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    From
                                </label>
                                <input
                                    id="geo-exposure-filter-from"
                                    type="date"
                                    v-model="filters.startDate"
                                    data-testid="geo-exposure-filter-from"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="geo-exposure-filter-to"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    To
                                </label>
                                <input
                                    id="geo-exposure-filter-to"
                                    type="date"
                                    v-model="filters.endDate"
                                    data-testid="geo-exposure-filter-to"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="geo-exposure-filter-flag"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Flag
                                </label>
                                <select
                                    id="geo-exposure-filter-flag"
                                    v-model="filters.flagKey"
                                    data-testid="geo-exposure-filter-flag"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All flags</option>
                                    <option
                                        v-for="flag in flags"
                                        :key="flag.key"
                                        :value="flag.key"
                                    >
                                        {{ flag.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="geo-exposure-apply-filters"
                                    @click="load"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load geographic exposure. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading">
                            <div
                                class="flex flex-wrap gap-3"
                                data-testid="geo-exposure-totals"
                            >
                                <div
                                    class="rounded-md border border-taupe-200 bg-white px-3 py-2 text-sm"
                                >
                                    <span class="text-taupe-500"
                                        >Total exposures:</span
                                    >
                                    <span class="font-medium text-taupe-800">{{
                                        totalExposures.toLocaleString()
                                    }}</span>
                                </div>
                                <div
                                    v-if="unknownTotal > 0"
                                    class="rounded-md border border-taupe-200 bg-white px-3 py-2 text-sm"
                                    data-testid="geo-exposure-unknown"
                                >
                                    <span class="text-taupe-500"
                                        >Not yet geocoded:</span
                                    >
                                    <span class="font-medium text-taupe-800">{{
                                        unknownTotal.toLocaleString()
                                    }}</span>
                                    <span class="text-taupe-500"
                                        >({{ unknownPercentage }}%)</span
                                    >
                                </div>
                            </div>

                            <div
                                class="rounded-md border border-taupe-200 bg-white p-4"
                                data-testid="geo-exposure-map"
                            >
                                <zen-empty-table-message
                                    v-if="countries.length === 0"
                                    compact
                                    data-testid="geo-exposure-empty"
                                    icon="ZenIconWorld"
                                >
                                    No geocoded exposure recorded for this date
                                    range.
                                </zen-empty-table-message>
                                <div v-else style="height: 420px">
                                    <Chart
                                        type="choropleth"
                                        :data="chartData"
                                        :options="chartOptions"
                                    />
                                </div>
                            </div>

                            <div
                                class="rounded-md border border-taupe-200 bg-white p-4"
                                data-testid="geo-exposure-breakdown"
                            >
                                <zen-empty-table-message
                                    v-if="countries.length === 0"
                                    compact
                                    icon="ZenIconWorld"
                                >
                                    No countries to show.
                                </zen-empty-table-message>

                                <ul v-else class="divide-y divide-taupe-100">
                                    <li
                                        v-for="country in countries"
                                        :key="country.country"
                                        class="py-3"
                                        :data-testid="
                                            'geo-exposure-row-' +
                                            country.country
                                        "
                                    >
                                        <div
                                            class="flex items-center justify-between text-sm"
                                        >
                                            <span
                                                class="font-medium text-taupe-800"
                                                >{{
                                                    countryName(country.country)
                                                }}</span
                                            >
                                            <div class="text-taupe-500">
                                                <span
                                                    class="font-medium text-taupe-800"
                                                    >{{
                                                        country.total.toLocaleString()
                                                    }}</span
                                                >
                                                ({{ country.percentage }}%)
                                            </div>
                                        </div>
                                        <div
                                            class="mt-1.5 h-1.5 w-full rounded-full bg-taupe-100"
                                        >
                                            <div
                                                class="h-1.5 rounded-full bg-sky-500"
                                                :style="{
                                                    width:
                                                        country.percentage +
                                                        '%',
                                                }"
                                            />
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </template>

                        <div v-if="!loading" class="text-sm">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                        <div
                            v-if="!loading"
                            class="text-xs text-taupe-500 pb-10"
                        >
                            Zenmanage uses
                            <a
                                href="https://www.ip2location.io"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="hover:underline"
                                >IP2Location.io</a
                            >
                            IP geolocation web service.
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import { Chart } from 'vue-chartjs'
import { Chart as ChartJS, Tooltip, Legend } from 'chart.js'
import {
    ChoroplethController,
    GeoFeature,
    ColorScale,
    ProjectionScale,
} from 'chartjs-chart-geo'

import ReportService from '@/services/report.service'
import FlagService from '@/services/flag.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { alpha2ToFeatureMap, countryName } from '@/utils/world-geo'

ChartJS.register(
    ChoroplethController,
    GeoFeature,
    ColorScale,
    ProjectionScale,
    Tooltip,
    Legend
)

// Neutral fill for countries with zero exposure in the selected range - close to
// the app's taupe-200 border color, distinct from the lightest step of the blue
// exposure scale so "no data" doesn't read as "very low data".
const NO_DATA_COLOR = '#e7e5e4'

export default {
    name: 'GeographicExposure',
    components: { Chart },
    setup() {
        const { current } = useCurrentEnvironment()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        return { current, hasPlanFeature, loading, error, run }
    },
    data() {
        return {
            totalExposures: 0,
            knownTotal: 0,
            unknownTotal: 0,
            unknownPercentage: 0,
            countries: [],
            flags: [],
            generatedAt: null,
            filters: {
                startDate: '',
                endDate: '',
                flagKey: '',
            },
        }
    },
    computed: {
        canView() {
            return this.hasPlanFeature('advanced_reporting')
        },
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        chartFeatures() {
            const featureMap = alpha2ToFeatureMap()
            const totalByCountry = new Map(
                this.countries.map((c) => [c.country, c.total])
            )

            // Countries absent from the report are left with an undefined value
            // (rather than 0) so the color scale renders them via `missing` instead
            // of folding them into the real data's min/max range - otherwise a
            // country with a small-but-real total (e.g. 5) would be
            // indistinguishable from the ~190 countries with no exposure at all.
            return Array.from(featureMap, ([code, feature]) => ({
                feature,
                value: totalByCountry.get(code),
                code,
            }))
        },
        chartData() {
            return {
                labels: this.chartFeatures.map((f) => countryName(f.code)),
                datasets: [
                    {
                        label: 'Exposures',
                        data: this.chartFeatures,
                    },
                ],
            }
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                showOutline: true,
                showGraticule: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const point = context.raw ?? {}
                                const total = point.value ?? 0

                                return `${countryName(point.code)}: ${total.toLocaleString()}`
                            },
                        },
                    },
                },
                scales: {
                    projection: {
                        axis: 'x',
                        projection: 'equalEarth',
                    },
                    color: {
                        axis: 'x',
                        quantize: 5,
                        interpolate: 'blues',
                        missing: NO_DATA_COLOR,
                        legend: {
                            position: 'bottom-right',
                        },
                    },
                },
            }
        },
    },
    watch: {
        environmentScopeKey() {
            this.refresh()
        },
    },
    methods: {
        countryName,
        defaultDateRange() {
            const end = new Date()
            const start = new Date()
            start.setDate(start.getDate() - 29)

            return {
                startDate: start.toISOString().slice(0, 10),
                endDate: end.toISOString().slice(0, 10),
            }
        },
        load() {
            if (!this.canView) return

            this.run(
                (signal) =>
                    ReportService.getGeographicExposure(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            startDate: this.filters.startDate,
                            endDate: this.filters.endDate,
                            flagKey: this.filters.flagKey || undefined,
                        },
                        { signal }
                    ),
                (response) => {
                    this.totalExposures = response.data.total_exposures
                    this.knownTotal = response.data.known_total
                    this.unknownTotal = response.data.unknown_total
                    this.unknownPercentage = response.data.unknown_percentage
                    this.countries = response.data.countries
                    this.generatedAt = response.data.generated_at
                },
                () => {
                    this.totalExposures = 0
                    this.knownTotal = 0
                    this.unknownTotal = 0
                    this.unknownPercentage = 0
                    this.countries = []
                }
            )
        },
        refresh() {
            this.load()

            if (!this.canView) return

            FlagService.list(this.current.projectKey)
                .then((response) => {
                    this.flags = response.data ?? []
                })
                .catch(() => {
                    this.flags = []
                })
        },
    },
    mounted() {
        Object.assign(this.filters, this.defaultDateRange())
        this.refresh()
    },
}
</script>
